import _ from 'lodash';
import AgentInboxItem from 'models/agent_inbox/agent_inbox_item';
import Conversation from 'models/conversation';
import ConversationItem from 'models/conversation_item';
import ConversationItemType from 'models/conversation_item_type';
import CustomerMergeConverter from 'scripts/application/dto_converters/customer_merge_converter';
import DtoConverter from 'scripts/application/dto_converters/customer_profile_converter';
import parseTopicParams from 'scripts/adapters/gateways/lib/standard_gateway/parse_topic_params';
import {
  getInboxItemIdFromConversationItem,
  reconcileInboxItem,
  shouldHandleItem,
} from 'actions/inbox/agent/agent_inbox_helpers';
import removeItemFromInbox from 'actions/inbox/lib/remove_item_from_inbox';

export default class AgentInboxObserver {
  constructor(context) {
    this.context = context;
  }

  getStore() {
    return this.context.stores.agentInboxItemsNew;
  }

  // Every inbox row subscribes to its customer's org-wide conversation and item topics, so a
  // broadcast may describe work that belongs to another agent. Those may update a row the
  // assignment broadcasts already created, but must never create one.
  onBroadcastConversation(originalTopic, { payload: conversationDto }, topic) {
    const { customerId } = parseTopicParams(originalTopic, topic);
    const conversation = Conversation.fromJs(conversationDto);
    if (!this.getStore().has({ id: customerId }) && !this.isNotClosedAndAssignedToCurrentAgent(conversation)) {
      return;
    }

    const updatedInboxItem = AgentInboxItem.create({
      id: customerId,
      type: AgentInboxItem.Type.CUSTOMER,
      conversation,
    });
    reconcileInboxItem(this.context, updatedInboxItem, this.getStore());
  }

  onBroadcastItem(originalTopic, { payload: itemDto }, topic) {
    const convoItem = ConversationItem.fromJs(itemDto);
    if (!shouldHandleItem(convoItem)) {
      return;
    }
    const isTask = convoItem.contentType() === ConversationItemType.TASK;
    const type = isTask ? AgentInboxItem.Type.TASK : AgentInboxItem.Type.CUSTOMER;
    const inboxItemId = getInboxItemIdFromConversationItem(convoItem);
    if (!this.getStore().has({ id: inboxItemId }) && !(isTask && this.isOpenTaskAssignedToCurrentAgent(convoItem))) {
      return;
    }

    const updatedInboxItem = AgentInboxItem.create({ id: inboxItemId, type, item: convoItem });
    reconcileInboxItem(this.context, updatedInboxItem, this.getStore());
  }

  onBroadcastProfile(originalTopic, { payload: profileDto }) {
    let profile = DtoConverter.fromDto(profileDto);
    const items = this.getStore().findAll({ profile: { id: profile.id } });

    _.forEach(items, item => {
      const updatedInboxItem = AgentInboxItem.create({ id: item.id, type: item.type, profile });
      reconcileInboxItem(this.context, updatedInboxItem, this.getStore());
    });
  }

  onBroadcastProfileMerge(originalTopic, { payload: mergeDto }, topic) {
    const mergeInfo = CustomerMergeConverter.fromDto(mergeDto);
    const oldCustomerId = mergeInfo.sourceCustomerId;
    removeItemFromInbox(this.context, oldCustomerId);
  }

  isNotClosedAndAssignedToCurrentAgent(conversation) {
    return conversation.status !== Conversation.Status.CLOSED && this.isAssignedToCurrentAgent(conversation.assignee);
  }

  isOpenTaskAssignedToCurrentAgent(taskItem) {
    return !taskItem.content.isClosed() && this.isAssignedToCurrentAgent(taskItem.content.assignee);
  }

  isAssignedToCurrentAgent(assignee) {
    const currentAgentId = this.context.stores.currentAgent.get().id;
    return !!assignee && !!assignee.agentId && assignee.agentId === currentAgentId;
  }
}
